import {
  Box,
  FieldSelect,
  FieldText,
  HelperField,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import omit from 'lodash/omit';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { useIsChanged } from 'components/powerFlowShared/hooks/useIsChanged';
import { AdapterTypes } from 'components/shared/announcementPicker/adapters';
import { AnnouncementItem } from 'components/shared/announcementPicker/adapters/announcementAdapter';
import { AnnouncementPicker } from 'components/shared/announcementPicker/announcementPicker';
import type {
  ActionNodeT,
  FlowManagerEditModalComponentT,
} from 'fable/flowEditor';

import type { FlowCallNodeIVR, Payload } from '../nodeTypes/ivr';

import { ButtonOption, Option } from './ivr/buttonOption';
import styles from './ivr.module.scss';

const BUTTONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];
const ANNOUNCEMENT_FIELD = 'announcement';
const OPTIONS_FIELD = 'options';

export type Props = FlowManagerEditModalComponentT<
  ActionNodeT | FlowCallNodeIVR
>;

type Errors = {
  [Key in typeof ANNOUNCEMENT_FIELD | typeof ANNOUNCEMENT_FIELD]?: string;
};

export function IVR({ node, replaceNode, triggerClose }: Props): JSX.Element {
  const {
    announcement: { file } = { file: null },
    buttons,
    default_node_id = '',
    repeat: payloadRepeat,
  } = {
    ...node.payload,
  };

  const initAnnouncement = {
    label: file?.pretty_name ?? '',
    url: file?.url ?? '',
    author: file?.author ?? '',
  };
  const initButtonOptions = BUTTONS.map((button: string) => {
    const { nodeId } = buttons?.[button] || {};

    return {
      button,
      active: !!nodeId,
      nodeId: nodeId || uuid(),
    };
  });
  const initDefaultNodeId = default_node_id || '';
  const initRepeat = payloadRepeat || 3;

  const [announcement, setAnnouncement] =
    useState<AnnouncementItem>(initAnnouncement);
  const [buttonOptions, setButtonOptions] =
    useState<Option[]>(initButtonOptions);
  const [defaultNodeId, setDefaultNodeId] = useState<string>(initDefaultNodeId);
  const [repeat, setRepeat] = useState<number>(initRepeat);
  const [errors, setErrors] = useState<Errors>({});

  const isChanged = useIsChanged(
    [initAnnouncement, announcement],
    [initButtonOptions, buttonOptions],
    [initDefaultNodeId, defaultNodeId],
    [initRepeat, repeat],
  );

  const hasActiveButton = useMemo(
    () => buttonOptions.some((option) => option.active),
    [buttonOptions],
  );

  const buttonItems = useMemo(
    () => [
      { id: '', name: 'Hang up' },
      ...buttonOptions.reduce(
        (
          buttons: { id: string; name: string }[],
          { button, nodeId, active },
        ) => {
          if (!active) return buttons;
          return [...buttons, { id: nodeId, name: `Press ${button}` }];
        },
        [],
      ),
    ],
    [buttonOptions],
  );

  useEffect(() => {
    if (announcement.url && errors[ANNOUNCEMENT_FIELD]) {
      setErrors((errors) => omit(errors, ANNOUNCEMENT_FIELD));
    }

    if (hasActiveButton && errors[OPTIONS_FIELD]) {
      setErrors((errors) => omit(errors, OPTIONS_FIELD));
    }
  }, [announcement, errors, hasActiveButton, setErrors]);

  const handleButtonChange = useCallback(
    (option: Option) => {
      setButtonOptions((bOptions) => {
        const newButtonOptions = [...bOptions];
        const index = newButtonOptions.findIndex(
          ({ button }) => button === option.button,
        );
        newButtonOptions[index] = option;
        return newButtonOptions;
      });
    },
    [setButtonOptions],
  );

  const handleDefaultOptionChange = useCallback(
    (value) => setDefaultNodeId(value),
    [setDefaultNodeId],
  );

  const handleRepeatChange = useCallback(
    (event?: ChangeEvent<HTMLInputElement>) => {
      if (!event) {
        return;
      }

      const {
        target: { value },
      } = event;

      setRepeat(parseInt(value));
    },
    [setRepeat],
  );

  const validate = useCallback(() => {
    const fieldErrors: Errors = {};

    if (!announcement.url) {
      fieldErrors[ANNOUNCEMENT_FIELD] = 'Please select an announcement file';
    }

    if (!hasActiveButton) {
      fieldErrors[OPTIONS_FIELD] = 'Please select at least one menu option';
    }

    setErrors(fieldErrors);

    return !Object.keys(fieldErrors).length;
  }, [announcement, hasActiveButton]);

  const handleSubmit = useCallback(() => {
    if (!validate()) {
      return;
    }

    const payload: Payload = {
      announcement: {
        file: announcement,
        tts: null,
      },
      buttons: buttonOptions.reduce((buttons, { active, button, nodeId }) => {
        if (active) {
          buttons[button] = {
            button,
            nodeId: nodeId ?? uuid(),
          };
        }

        return buttons;
      }, {}),
      repeat,
      default_node_id: defaultNodeId,
    };

    replaceNode({
      ...node,
      icon: 'i-channels',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.IVR_MENU,
      overlay: [],
      payload,
      text: 'IVR',
    });
  }, [
    announcement,
    buttonOptions,
    defaultNodeId,
    node,
    repeat,
    replaceNode,
    validate,
  ]);

  return (
    <Box width="600px">
      <ModalHead title="Interactive Voice Reponse (IVR)" onClose={triggerClose}>
        <Text.Paragraph className={styles.description}>
          Give incoming callers options to choose from using their dial pad
          before talking to an agent.
        </Text.Paragraph>
      </ModalHead>
      <ModalBody>
        <form>
          <AnnouncementPicker
            adapter={AdapterTypes.Announcement}
            className={styles.announcementPicker}
            onSelect={setAnnouncement}
            selectedAudio={announcement}
            fieldProps={{
              meta: { error: errors[ANNOUNCEMENT_FIELD] },
            }}
          />
          <div className={styles.options}>
            <h5 className={styles.optionsTitle}>Menu options</h5>
            <div className={styles.optionsWrapper}>
              {buttonOptions.map((buttonOption: Option) => (
                <ButtonOption
                  key={buttonOption.button}
                  onChange={handleButtonChange}
                  option={buttonOption}
                />
              ))}
            </div>
          </div>
          <HelperField
            className={styles.optionsError}
            errorMessage={errors[OPTIONS_FIELD]}
            fieldId="options"
          >
            <></>
          </HelperField>
          <FieldSelect
            className={styles.defaultOption}
            input={{
              name: 'default option',
              onChange: handleDefaultOptionChange,
              value: defaultNodeId,
            }}
            items={buttonItems}
            label="Default option"
            noAlert
          />
          <FieldText
            input={{
              name: 'repeat',
              onChange: handleRepeatChange,
              value: repeat.toString(),
            }}
            label="Repeat"
            max={10}
            min={1}
            noAlert
            suffix="Times"
            type="number"
          />
        </form>
      </ModalBody>
      <ModalFoot
        confirmOnClick={handleSubmit}
        confirmName={getConfirmButtonVerb(node)}
        confirmProps={{ disabled: !isChanged }}
        cancelOnClick={triggerClose}
      />
    </Box>
  );
}
